// State for the v2 drilldown (person, institution and funder subjects behind their flags; see
// services/drilldown/routing). Drilldown visibility, history and the entity being viewed still live in the entity
// module's entityDrillDownConfig; this module holds v2-only state. Filters and the active tab are saved on the current
// history entry so Back/Forward restore them.
import EntityService from 'src/api/entity';
import DocumentService from 'src/api/documents';
import { buildPublicationsRequest, buildPatentsRequest } from 'src/services/drilldown/documentQuery';

// One Classic view preference for every subject. The key predates the institution subject; kept so existing
// preferences survive.
const CLASSIC_VIEW_STORAGE_KEY = 'bs.personDrilldown.classic';

// localStorage can be unavailable (private mode, blocked site data), so fall back to the v2 view
function readClassicPreference() {
  try {
    return localStorage.getItem(CLASSIC_VIEW_STORAGE_KEY) === 'true';
  } catch (e) {
    return false;
  }
}

function writeClassicPreference(value) {
  try {
    localStorage.setItem(CLASSIC_VIEW_STORAGE_KEY, String(value));
  } catch (e) {
    // Preference just won't survive a reload
  }
}

// null years = all years; [] advisory types = all types
export const defaultFilters = () => ({
  minYear: null,
  maxYear: null,
  advisoryTypes: [],
  advisoriesOnly: false,
});

function isDefaultFilters(filters) {
  return (
    filters.minYear == null &&
    filters.maxYear == null &&
    filters.advisoryTypes.length === 0 &&
    !filters.advisoriesOnly
  );
}

// Document lists on the Publications and Patents tabs. sharedWith is the local filter from a View button (never saved
// to history and never sent with the counts request). sort is the search service's sort key. stale = refetch when shown.
const defaultDocs = () => ({
  items: [],
  total: 0,
  page: 1,
  rowsPerPage: 10,
  sort: 'Year',
  loading: false,
  stale: true,
  sharedWith: null,
  sharedWithLabel: '',
});
const defaultDocLists = () => ({ publications: defaultDocs(), patents: defaultDocs() });

// Right-hand panels on the Publications and Patents tabs
const defaultPanel = () => ({ data: null, loading: false, stale: true });
const defaultPanels = () => ({ publications: defaultPanel(), patents: defaultPanel() });

// Affiliations, Funding associations, People and Partner institutions tables from /drilldown/table. A person's tables
// load every row (paged and sorted in the browser); an institution's or funder's People, Funding associations
// (Co-funders) and Partner institutions (a funder's Institutions), with tens of thousands of rows, are paged on the
// server: one page at a time, with rowsNumber pageable rows of total. The paging fields are unused for browser-paged
// tables.
const defaultTable = () => ({
  rows: [],
  truncated: false,
  loading: false,
  stale: true,
  page: 1,
  rowsPerPage: 25,
  search: '',
  sort: 'advisories',
  rowsNumber: 0,
  total: 0,
});
const defaultTables = () => ({
  affiliations: defaultTable(),
  funding: defaultTable(),
  people: defaultTable(),
  // The Collaborations tab's controls also filter Partner institutions by country and advisories; the countries
  // offered come back with each page
  partners: { ...defaultTable(), countries: [], advisoriesOnly: false, countryOptions: [] },
});
const SERVER_TABLES = {
  person: [],
  institution: ['people', 'funding', 'partners'],
  funder: ['people', 'funding', 'partners'],
};

// Collaborations tab data (graph and table share it)
const defaultCollaborations = () => ({ data: null, loading: false, stale: true });

// Request subject for the entity service. Author / inventor and institution / assignee ids live in separate id spaces;
// the cross-type merges ('person', 'organization') carry both lists.
function subjectFor(config) {
  switch (config.entityType) {
    case 'person':
      return { type: 'person', ids: config.personIds };
    case 'organization':
      return { type: 'institution', ids: config.orgIds };
    case 'institution':
      return { type: 'institution', ids: { institution: config.entityId, assignee: [] } };
    case 'funder':
      return { type: 'funder', ids: { funder: config.entityId } };
    default:
      return {
        type: 'person',
        ids: {
          author: config.entityType === 'author' ? config.entityId : [],
          inventor: config.entityType === 'inventor' ? config.entityId : [],
        },
      };
  }
}

// Scoped to the scan query when the scope toggle is on, otherwise the whole person
function scopeFor(config, scanType) {
  return config.queryScope && config.query ? { query: config.query, scan_type: scanType || 'boolean' } : null;
}

const state = {
  classic: readClassicPreference(),
  subject: null,
  request: null,
  filters: defaultFilters(),
  activeTab: null,
  header: null,
  headerLoading: false,
  connections: null,
  connectionsLoading: false,
  // Connection count under default filters: decides whether the Advisories tab exists and the default tab
  baselineConnections: null,
  docs: defaultDocLists(),
  panels: defaultPanels(),
  tables: defaultTables(),
  collaborations: defaultCollaborations(),
  error: null,
  // Request sequence numbers: a response is ignored if a newer request of the same kind has started
  seq: {},
};

const getters = {
  classic: (state) => state.classic,
  subject: (state) => state.subject,
  // { subject: { type, ids: { author, inventor } }, scope } as sent to the entity service
  request: (state) => state.request,
  filters: (state) => state.filters,
  header: (state) => state.header,
  headerLoading: (state) => state.headerLoading,
  connections: (state) => state.connections,
  connectionsLoading: (state) => state.connectionsLoading,
  docs: (state) => state.docs,
  panels: (state) => state.panels,
  tables: (state) => state.tables,
  collaborations: (state) => state.collaborations,
  error: (state) => state.error,
  // The subject's own advisories (institutions, funders); not connections, but they create the Advisories tab
  hasOwnAdvisories: (state) => !!(state.header && state.header.ownAdvisories && state.header.ownAdvisories.length),
  hasAdvisoriesTab: (state, getters) => state.baselineConnections > 0 || getters.hasOwnAdvisories,
  // Researchers with a flagged (other) affiliation, from the connections (an institution's dual-affiliated partner
  // co-authors; a funder's researchers listed at a flagged institution): the People table lists them first
  advisoryPersonIds: (state) => (state.connections && state.connections.advisoryPersonIds) || [],
  // Whether this subject's table of this kind is paged on the server
  isServerTable: (state) => (kind) => !!state.request && SERVER_TABLES[state.request.subject.type].includes(kind),
  // Default tab: Advisories when the subject has any advisory connection (or advisories of its own: an institution's,
  // or a merged sanctions record's), otherwise Publications. null until that's known so the panels don't flash the
  // wrong tab; subjects that can have their own advisories also wait for the header, which carries them.
  activeTab: (state, getters) => {
    const subject = state.request && state.request.subject;
    const ownAdvisoriesPossible =
      !!subject && (subject.type !== 'person' || !!(subject.ids.sanction && subject.ids.sanction.length));
    const waitingForHeader = ownAdvisoriesPossible && !state.header;
    if (state.baselineConnections == null || (waitingForHeader && !state.activeTab)) {
      return state.activeTab || (state.error ? 'publications' : null);
    }
    if (state.activeTab && (state.activeTab !== 'advisories' || getters.hasAdvisoriesTab)) {
      return state.activeTab;
    }
    return getters.hasAdvisoriesTab ? 'advisories' : 'publications';
  },
};

// Start a request of the given kind and return a function that tells whether it's still the latest one
function startRequest(commit, state, key) {
  const seq = (state.seq[key] || 0) + 1;
  commit('setSeq', { key, seq });
  return () => state.seq[key] === seq;
}

const actions = {
  // Entry point from entity/setEntityDrillDownConfig when the drilldown routes to v2. Runs for a new person, a
  // history restore, a scope change, and a Classic view switch back to v2.
  open({ commit, dispatch, state, rootGetters }, config) {
    const saved = rootGetters['entity/entityDrillDownHistory'][config.historyIndex] || {};
    // Restore saved filters; otherwise a person opened from inside the drilldown inherits the current filters,
    // and a fresh open (dialog was closed, which resets this module) starts from the defaults
    const filters = saved.drilldownFilters
      ? { ...saved.drilldownFilters }
      : state.subject
      ? { ...state.filters }
      : defaultFilters();
    // A scope change or Classic switch reopens the same subject: keep its header (name, own advisories) on screen
    // while the scoped stats reload, instead of falling back to the opener's raw name
    const sameSubject =
      !!state.subject &&
      state.subject.entityType === config.entityType &&
      JSON.stringify(state.subject.entityId) === JSON.stringify(config.entityId);

    commit('setSubject', {
      entityType: config.entityType,
      entityId: config.entityId,
      entityName: config.entityName,
      mergeMode: !!config.mergeMode,
      queryScope: !!config.queryScope,
      hideQueryScopeToggle: !!config.hideQueryScopeToggle,
    });
    commit('setRequest', {
      subject: subjectFor(config),
      scope: scopeFor(config, rootGetters['scans/scanType']),
    });
    commit('setFilters', filters);
    commit('setActiveTab', saved.drilldownTab || null);
    commit('setBaselineConnections', null);
    if (!sameSubject) commit('setHeader', null);
    commit('setConnections', null);
    commit('setDocLists', defaultDocLists());
    commit('setPanels', defaultPanels());
    commit('setTables', defaultTables());
    commit('setCollaborations', defaultCollaborations());
    commit('setError', null);
    commit('entity/updateCurrentHistoryEntry', { drilldownFilters: filters }, { root: true });

    dispatch('fetchHeader');
    dispatch('fetchConnections');
  },

  async fetchHeader({ commit, state }) {
    const isLatest = startRequest(commit, state, 'header');
    commit('setHeaderLoading', true);
    try {
      const response = await new EntityService().getDrilldownHeader(state.request);
      if (!isLatest()) return;
      if (response.error) throw new Error(response.message || 'Header request failed');
      commit('setHeader', Object.freeze(response.data));
    } catch (e) {
      if (isLatest()) commit('setError', e.message);
    } finally {
      if (isLatest()) commit('setHeaderLoading', false);
    }
  },

  async fetchConnections({ commit, state }) {
    const isLatest = startRequest(commit, state, 'connections');
    commit('setConnectionsLoading', true);
    const service = new EntityService();
    const filters = { ...state.filters };
    try {
      // The Advisories tab depends on connections under default filters; fetch them once if the current filters differ
      const needsBaseline = state.baselineConnections == null && !isDefaultFilters(filters);
      const [response, baseline] = await Promise.all([
        service.getDrilldownConnections({ ...state.request, filters }),
        needsBaseline ? service.getDrilldownConnections({ ...state.request, filters: defaultFilters() }) : null,
      ]);
      if (!isLatest()) return;
      if (response.error) throw new Error(response.message || 'Connections request failed');
      commit('setConnections', Object.freeze(response.data));
      if (isDefaultFilters(filters)) {
        commit('setBaselineConnections', response.data.summary.connections);
      } else if (baseline && !baseline.error) {
        commit('setBaselineConnections', baseline.data.summary.connections);
      }
    } catch (e) {
      if (isLatest()) commit('setError', e.message);
    } finally {
      if (isLatest()) commit('setConnectionsLoading', false);
    }
  },

  // Merge a partial filter change, save it on the history entry, refresh the counts and mark every list stale
  setFilters({ commit, dispatch, state }, partial) {
    const filters = { ...state.filters, ...partial };
    commit('setFilters', filters);
    commit('entity/updateCurrentHistoryEntry', { drilldownFilters: filters }, { root: true });
    for (const tab of ['publications', 'patents']) {
      commit('updateDocs', { tab, page: 1, stale: true });
      commit('updatePanel', { tab, stale: true });
    }
    commit('updateTable', { kind: 'affiliations', stale: true });
    for (const kind of ['funding', 'people', 'partners']) commit('updateTable', { kind, page: 1, stale: true });
    commit('updateCollaborations', { stale: true });
    dispatch('fetchConnections');
  },

  async fetchCollaborations({ commit, state }) {
    const isLatest = startRequest(commit, state, 'collaborations');
    commit('updateCollaborations', { loading: true, stale: false });
    try {
      const response = await new EntityService().getDrilldownCollaborations({ ...state.request, filters: { ...state.filters } });
      if (!isLatest()) return;
      if (response.error) throw new Error(response.message || 'Collaborations request failed');
      commit('updateCollaborations', { data: Object.freeze(response.data) });
    } catch (e) {
      if (isLatest()) commit('setError', e.message);
    } finally {
      if (isLatest()) commit('updateCollaborations', { loading: false });
    }
  },

  // One /drilldown/table request under the current filters, returning its data. Server-paged tables send the table's
  // page and search (or the given page, e.g. a larger one for CSV export); People also sends its advisory rows, which
  // come from the connections.
  async requestTable({ state, getters }, { kind, ...pageOverride }) {
    const table = state.tables[kind];
    const paging = getters.isServerTable(kind)
      ? {
          page: table.page,
          rowsPerPage: table.rowsPerPage,
          search: table.search,
          sort: table.sort,
          ...(kind === 'people' ? { advisoryPersonIds: getters.advisoryPersonIds } : {}),
          ...(kind === 'partners' ? { countries: table.countries, advisoriesOnly: table.advisoriesOnly } : {}),
          ...pageOverride,
        }
      : {};
    const response = await new EntityService().getDrilldownTable({
      ...state.request,
      filters: { ...state.filters },
      kind,
      ...paging,
    });
    if (response.error) throw new Error(response.message || 'Table request failed');
    return response.data;
  },

  // Fetch every row of the Affiliations ('affiliations') or Funding associations ('funding') table, or the current page
  // of a server-paged table ('people'), with its advisory rows listed first
  async fetchTable({ commit, state, dispatch }, kind) {
    const isLatest = startRequest(commit, state, `table-${kind}`);
    commit('updateTable', { kind, loading: true, stale: false });
    try {
      const { rows, ...rest } = await dispatch('requestTable', { kind });
      if (!isLatest()) return;
      commit('updateTable', { kind, rows: Object.freeze(rows), ...rest });
    } catch (e) {
      if (isLatest()) commit('setError', e.message);
    } finally {
      if (isLatest()) commit('updateTable', { kind, loading: false });
    }
  },

  // Server-paged tables: a new page, page size, search or sort ({ page, rowsPerPage, search, sort }), or Partner
  // institutions' countries or advisoriesOnly; anything but a page change starts again from page 1
  setTablePaging({ commit, state }, { kind, ...paging }) {
    const table = state.tables[kind];
    const changed = (key) => key in paging && paging[key] !== table[key];
    const reset = ['search', 'rowsPerPage', 'sort', 'countries', 'advisoriesOnly'].some(changed);
    commit('updateTable', { kind, ...paging, ...(reset ? { page: 1 } : {}), stale: true });
  },

  // One page of the Publications or Patents list. direction 'next' / 'previous' pages from the current page with
  // search_after (the search service only supports sequential paging); no direction loads page 1.
  async fetchDocuments({ commit, state, rootGetters }, { tab, direction }) {
    const isLatest = startRequest(commit, state, `docs-${tab}`);
    const current = state.docs[tab];
    const items = current.items;
    const page = direction === 'next' ? current.page + 1 : direction === 'previous' ? current.page - 1 : 1;
    // Patents carry no advisories, so "Advisories only" leaves the patent list empty without a request
    if (tab === 'patents' && state.filters.advisoriesOnly) {
      commit('updateDocs', { tab, items: [], total: 0, page: 1, stale: false });
      return;
    }
    const pagination = {
      page,
      rowsPerPage: current.rowsPerPage,
      size: current.rowsPerPage,
      sort: current.sort,
      sortBy: 'desc',
      direction: direction || 'next',
      firstDocument: direction && items.length ? items[0].sort : null,
      lastDocument: direction && items.length ? items[items.length - 1].sort : null,
    };
    const scanDetails = rootGetters['scans/getScanDetails'];
    const common = {
      subject: state.request.subject,
      scope: state.request.scope,
      filters: state.filters,
      sharedWith: current.sharedWith,
      pagination,
      scanId: scanDetails && scanDetails.version_id,
    };
    const request = tab === 'patents' ? buildPatentsRequest(common) : buildPublicationsRequest(common);
    commit('updateDocs', { tab, loading: true, stale: false });
    try {
      const { documents, doc_count, error } = await new DocumentService().fetchDocuments(request);
      if (!isLatest()) return;
      if (error) throw new Error(typeof error === 'string' ? error : `${tab} request failed`);
      commit('updateDocs', { tab, items: Object.freeze(documents || []), total: doc_count || 0, page });
    } catch (e) {
      if (isLatest()) commit('setError', e.message);
    } finally {
      if (isLatest()) commit('updateDocs', { tab, loading: false });
    }
  },

  setDocumentsPageSize({ commit }, { tab, rowsPerPage }) {
    commit('updateDocs', { tab, rowsPerPage, page: 1, stale: true });
  },

  setDocumentsSort({ commit }, { tab, sort }) {
    commit('updateDocs', { tab, sort, page: 1, stale: true });
  },

  async fetchPanels({ commit, state }, tab) {
    const isLatest = startRequest(commit, state, `panels-${tab}`);
    commit('updatePanel', { tab, loading: true, stale: false });
    try {
      const response = await new EntityService().getDrilldownPanels({
        ...state.request,
        filters: { ...state.filters },
        kind: tab,
      });
      if (!isLatest()) return;
      if (response.error) throw new Error(response.message || 'Panels request failed');
      commit('updatePanel', { tab, data: response.data ? Object.freeze(response.data) : null });
    } catch (e) {
      if (isLatest()) commit('setError', e.message);
    } finally {
      if (isLatest()) commit('updatePanel', { tab, loading: false });
    }
  },

  // From a View button: open the Publications (or Patents) tab filtered to the documents shared with one entity or pair
  openSharedWith({ commit, dispatch }, { tab = 'publications', sharedWith, label }) {
    commit('updateDocs', { tab, sharedWith, sharedWithLabel: label, page: 1, items: [], stale: true });
    dispatch('setActiveTab', tab);
  },

  clearSharedWith({ commit }, tab) {
    commit('updateDocs', { tab, sharedWith: null, sharedWithLabel: '', page: 1, stale: true });
  },

  // Open another entity's drilldown from a table link, carrying over the scope settings. Assignees live in the patent index.
  openEntity({ dispatch, rootGetters }, { entityType, id, name }) {
    const config = rootGetters['entity/entityDrillDownConfig'];
    dispatch(
      'entity/setEntityDrillDownConfig',
      {
        entityId: [id],
        entityName: [name],
        entityType,
        entitySubType: '',
        visible: true,
        query: config.queryScope ? config.query : null,
        queryScope: config.queryScope,
        hideQueryScopeToggle: config.hideQueryScopeToggle,
        // The config mutation spreads the previous config, so clear merge state explicitly
        mergeMode: false,
        source: entityType === 'assignee' || entityType === 'inventor' ? 'patent' : 'publication',
      },
      { root: true }
    );
  },

  setActiveTab({ commit }, tab) {
    commit('setActiveTab', tab);
    commit('entity/updateCurrentHistoryEntry', { drilldownTab: tab }, { root: true });
  },

  // Called when the drilldown closes so the next open starts from default filters
  reset({ commit }) {
    commit('setSubject', null);
    commit('setRequest', null);
    commit('setFilters', defaultFilters());
    commit('setActiveTab', null);
    commit('setHeader', null);
    commit('setConnections', null);
    commit('setBaselineConnections', null);
    commit('setDocLists', defaultDocLists());
    commit('setPanels', defaultPanels());
    commit('setTables', defaultTables());
    commit('setCollaborations', defaultCollaborations());
    commit('setError', null);
  },

  // Flip between the legacy and v2 views, then reload the current drilldown so the matching loaders run
  setClassicView({ commit, dispatch }, value) {
    writeClassicPreference(value);
    commit('setClassic', value);
    dispatch('entity/reloadCurrent', null, { root: true });
  },
};

const mutations = {
  setClassic(state, payload) {
    state.classic = payload;
  },
  setSubject(state, payload) {
    state.subject = payload;
  },
  setRequest(state, payload) {
    state.request = payload;
  },
  setFilters(state, payload) {
    state.filters = payload;
  },
  setActiveTab(state, payload) {
    state.activeTab = payload;
  },
  setHeader(state, payload) {
    state.header = payload;
  },
  setHeaderLoading(state, payload) {
    state.headerLoading = payload;
  },
  setConnections(state, payload) {
    state.connections = payload;
  },
  setConnectionsLoading(state, payload) {
    state.connectionsLoading = payload;
  },
  setBaselineConnections(state, payload) {
    state.baselineConnections = payload;
  },
  setError(state, payload) {
    state.error = payload;
  },
  setSeq(state, { key, seq }) {
    state.seq = { ...state.seq, [key]: seq };
  },
  setDocLists(state, payload) {
    state.docs = payload;
  },
  updateDocs(state, { tab, ...payload }) {
    state.docs = { ...state.docs, [tab]: { ...state.docs[tab], ...payload } };
  },
  setPanels(state, payload) {
    state.panels = payload;
  },
  updatePanel(state, { tab, ...payload }) {
    state.panels = { ...state.panels, [tab]: { ...state.panels[tab], ...payload } };
  },
  setTables(state, payload) {
    state.tables = payload;
  },
  updateTable(state, { kind, ...payload }) {
    state.tables = { ...state.tables, [kind]: { ...state.tables[kind], ...payload } };
  },
  setCollaborations(state, payload) {
    state.collaborations = payload;
  },
  updateCollaborations(state, payload) {
    state.collaborations = { ...state.collaborations, ...payload };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
